import React, {
    useState,
    useMemo,
    ReactElement,
    useEffect,
    useContext,
} from 'react';
import {useIntl} from 'react-intl';
import {CARET_DOWN__16} from '@homeaway/svg-defs';
import {
    TrackingContextValue,
    TrackingContext,
} from '@homeaway/react-event-tracking';

import FilterPill from '../FilterPill/FilterPill';
import FilterGroupModal from '../FilterGroupModal/FilterGroupModal';
import FilterGroupDropdown from '../FilterGroupDropdown/FilterGroupDropdown';
import FilterGroupContent from '../FilterGroupContent/FilterGroupContent';
import {
    getFilterGroupPillLabel,
    getAppliedCount,
} from '../../utils/filterPillUtils';
import {FILTER_GROUP_PRESENTED} from '../../utils/edap';

import type {FilterGroupFiltersProps} from '../FilterGroupFilters/FilterGroupFilters';
import type {FilterBarProps} from '../FilterBar/FilterBar';

type ExtendedProp = Pick<
    FilterBarProps,
    | 'appliedFilters'
    | 'currency'
    | 'customFiltersParams'
    | 'isMobile'
    | 'onClear'
    | 'onDismiss'
    | 'onDone'
    | 'onFilterToggle'
    | 'onMultipleFilterToggle'
    | 'priceFilterOptions'
    | 'priceInputValuesAreValid'
    | 'results'
    | 'selectedFilters'
    | 'onCustomFilterUpdate'
    | 'updatePriceInputValuesValidity'
>;
export interface FilterGroupPillProps
    extends ExtendedProp,
        FilterGroupFiltersProps {
    label: string;
    secondaryLabel?: string;
    scrolling: boolean;
    rank: number;
}

export default function FilterGroupPill(props: FilterGroupPillProps) {
    const {
        groupId,
        label,
        isMobile,
        filters,
        appliedFilters,
        customFiltersParams,
        currency,
        priceFilterOptions = {},
        rank,
    } = props;
    const [isGroupOpen, setIsGroupOpen] = useState(false);
    const {formatMessage, locale} = useIntl();
    const edapContext: TrackingContextValue = useContext(TrackingContext);

    const filterPillLabel = useMemo<string>(
        () =>
            getFilterGroupPillLabel({
                appliedFilters,
                filters,
                groupId,
                label,
                locale,
                formatMessage,
                currency,
                customFiltersParams,
                priceFilterOptions,
            }),
        [
            appliedFilters,
            filters,
            groupId,
            label,
            locale,
            formatMessage,
            currency,
            customFiltersParams,
            priceFilterOptions,
        ]
    );

    const appliedCount = useMemo<number>(
        () =>
            getAppliedCount({
                appliedFilters,
                filters,
                groupId,
                customFiltersParams,
            }),
        [appliedFilters, filters, groupId, customFiltersParams]
    );

    const filterPill = useMemo<ReactElement>(() => {
        return (
            <FilterPill
                label={filterPillLabel}
                icon={CARET_DOWN__16}
                rotateIcon={isGroupOpen}
                focused={isGroupOpen}
                appliedCount={appliedCount}
                applied={appliedCount > 0 || filterPillLabel !== label}
            />
        );
    }, [filterPillLabel, label, isGroupOpen, appliedCount]);

    const ComponentType = isMobile ? FilterGroupModal : FilterGroupDropdown;

    useEffect(() => {
        if (isGroupOpen) {
            edapContext.trigger(FILTER_GROUP_PRESENTED, {
                filtergroupid: groupId,
                actionlocation: 'filter bar',
                rank: `${rank}`,
            });
        }
    }, [isGroupOpen, rank, edapContext, groupId]);

    return (
        <ComponentType
            {...props}
            filterPill={filterPill}
            setIsGroupOpen={setIsGroupOpen}
        >
            <FilterGroupContent
                {...props}
                isGroupOpen={isGroupOpen}
                rank={rank}
            />
        </ComponentType>
    );
}
